import type * as React from 'react';

import expandArrowIcon from '../../assets/images/expand-arrow-icon.svg';
import sidebarToggleIcon from '../../assets/images/sidebar-toggle-icon.svg';
import { IconButton } from '../../atoms/IconButton';
import { Logo } from '../../atoms/Logo';
import { cn } from '../../lib/utils';
import { SidebarNavItem } from '../../molecules/SidebarNavItem';

export interface SidebarNavItemConfig {
  id: string;
  label: string;
  /** Pre-rendered icon node. The app owns icon resolution. */
  icon: React.ReactNode;
  /** Optional pre-rendered icon for the active state. Falls back to `icon`. */
  iconActive?: React.ReactNode;
  /** Optional pre-rendered icon for the disabled state. Falls back to `icon`. */
  iconDisabled?: React.ReactNode;
  /** Opaque to @dawn/ui — passed back to onNavigate for the app to use. */
  path: string;
  enabled: boolean;
}

export interface SidebarProps {
  items: SidebarNavItemConfig[];
  activeItemId: string | null;
  onNavigate: (item: SidebarNavItemConfig) => void;
  isOpen: boolean;
  onToggle: () => void;
  /** Rendered below the main list, typically the Settings nav item. */
  footer?: React.ReactNode;
  /**
   * Story / dev only. Forces the closed-state peek expand button into its
   * visible state so Storybook autodocs can document the visual without
   * requiring a real hover. Has no effect when `isOpen` is true. Do NOT use
   * in production code paths. (See `patterns.md` Common Gotcha: "Storybook
   * for organisms with hover popovers".)
   */
  forcePeekVisible?: boolean;
  /**
   * Story / dev only. Alias for `forcePeekVisible` per design.md § 3.5 /
   * decision log #25 — both names refer to the same hover-trigger surface.
   * Do NOT use in production code paths.
   */
  forceHoverOpen?: boolean;
}

export const SIDEBAR_WIDTH_OPEN = 210;
export const SIDEBAR_WIDTH_CLOSED = 58;

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeItemId,
  onNavigate,
  isOpen,
  onToggle,
  footer,
  forcePeekVisible,
  forceHoverOpen,
}) => {
  const sidebarWidth = isOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;
  const peekForced = Boolean(forcePeekVisible || forceHoverOpen);

  return (
    <div
      className="group fixed left-0 top-0 h-screen bg-dawn-black flex flex-col items-start justify-between z-50"
      style={{
        width: sidebarWidth,
        transition: 'width 200ms ease-in-out',
        overflow: isOpen ? 'hidden' : 'visible',
      }}
    >
      {/* Logo + toggle section */}
      <div
        className="flex items-center h-[70px] w-full"
        style={{ padding: '20px 12px', gap: isOpen ? '39px' : '0' }}
      >
        <Logo collapsed={!isOpen} />
        {isOpen && (
          <IconButton variant="on-dark" size="md" aria-label="Collapse sidebar" onClick={onToggle}>
            <img src={sidebarToggleIcon} alt="" className="w-[16px] h-[16px]" />
          </IconButton>
        )}
      </div>

      {/* Nav list + footer */}
      <div
        className="flex-1 flex flex-col justify-between w-full min-h-0"
        style={{ padding: '20px 4px' }}
      >
        <div className="flex flex-col gap-[2px] w-full">
          {items.map((item) => (
            <SidebarNavItem
              key={item.id}
              icon={item.icon}
              iconActive={item.iconActive}
              iconDisabled={item.iconDisabled}
              label={item.label}
              active={item.id === activeItemId}
              disabled={!item.enabled}
              collapsed={!isOpen}
              onClick={() => onNavigate(item)}
            />
          ))}
        </div>
        {footer}
      </div>

      {/* Peek expand button — only rendered when collapsed, visible on hover.
          Uses CSS :hover via Tailwind's `group-hover` (not JS state) so it
          stays visible when the mouse moves from the sidebar body onto the
          button. Positioned `fixed` with an explicit z-index so the 12px
          overhang into the main content area is painted above it.
          The `hover:bg-dawn-black` override is critical — without it, the
          IconButton variant's `hover:bg-white/10` makes the background
          semi-transparent on hover and the cream main content shows through. */}
      {!isOpen && (
        <IconButton
          variant="on-dark"
          size="md"
          aria-label="Expand sidebar"
          onClick={onToggle}
          className={cn(
            'fixed bg-dawn-black hover:bg-dawn-black',
            // Story-only forced state pins the button visible. Production path
            // keeps the hover-only `group-hover:inline-flex` behaviour.
            peekForced ? 'inline-flex' : 'hidden group-hover:inline-flex'
          )}
          style={{ left: `${SIDEBAR_WIDTH_CLOSED - 12}px`, top: '23px', zIndex: 60 }}
        >
          <img
            src={expandArrowIcon}
            alt=""
            className="w-[16px] h-[16px]"
            style={{ transform: 'rotate(180deg) scaleY(-1)' }}
          />
        </IconButton>
      )}
    </div>
  );
};

Sidebar.displayName = 'Sidebar';
